import React from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {generatePath} from 'react-router-dom';

import {ReactComponent as SettingsIcon} from 'assets/svg/options.svg';

import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {onSetEditCustoms} from 'models/customs/grid/table';
import {GridFieldConfig, UserPermission} from 'types';
import {Customs, CustomsRow} from 'types/profile';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {Wrapper} from '../styled';

import {Action, ActionsContainer, ControlsWrapper, HeaderControls} from './styled';
import {CustomsGridTable} from './Table';
import {TableSearch} from './Table/SearchFilter';

export function convertData(
    data: CustomsRow[] | undefined,
    updateCustoms: () => void,
    permissions?: UserPermission[],
): CustomsRow[] {
    if (!data) return [];

    return data.map((elem) => ({
        number: handleNil(elem.number),
        name: elem.id ? (
            permissions && permissions.includes(UserPermission.CUSTOMS_EMPLOYEE_LIST) ? (
                <Link
                    to={{
                        pathname: generatePath(ROUTE.CUSTOMS_USERS, {id: elem.id}),
                    }}
                >
                    {elem.name}
                </Link>
            ) : (
                elem.name
            )
        ) : (
            <div>—</div>
        ),
        address: handleNil(elem.address),
        actions: handleNil(elem, (value: Customs) => (
            <ActionsContainer>
                <Action
                    onClick={() => {
                        onSetEditCustoms(value);
                        updateCustoms();
                    }}
                >
                    <Icon source={SettingsIcon} fillColor="#000" width={18} height={18} />
                </Action>
            </ActionsContainer>
        )),
    }));
}

const getGridColumns = (t: TFunction, permissions?: UserPermission[]): GridFieldConfig[] => [
    {
        id: 'number',
        header: t('pages.customs.fields.number'),
        isSortable: true,
    },
    {
        id: 'name',
        header: t('pages.customs.fields.name'),
        isSortable: true,
    },
    {
        id: 'address',
        header: t('pages.customs.fields.address'),
        isSortable: true,
    },
    ...(permissions && permissions.includes(UserPermission.CUSTOMS_EDIT)
        ? [
              {
                  id: 'actions',
                  header: t('pages.customs.fields.actions'),
                  width: 60,
              },
          ]
        : []),
];

export const CustomsPage = () => {
    const {t} = useTranslation();

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                <PermissionAccess permissions={[UserPermission.CUSTOMS_CREATE]}>
                    <Button onClick={() => history.push(ROUTE.CUSTOMS_CREATE)}>
                        {t('pages.customs.buttons.create')}
                    </Button>
                </PermissionAccess>
            </ControlsWrapper>
        </HeaderControls>
    );

    return (
        <Page title={t('pages.customs.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <CustomsGridTable
                        configFunc={getGridColumns}
                        dataMapper={convertData}
                        headerControls={headerControls}
                    />
                </ContentContainer>
            </Wrapper>
        </Page>
    );
};
